"use client";

import {
  Avatar,
  Card,
  LiquidPill,
  LiquidSelectList,
} from "components/ui/v2";
import {
  AtSign,
  BadgeCheck,
  Cake,
  Github,
  Instagram,
  Languages as LanguagesIcon,
  Linkedin,
  Link2,
  Mail,
} from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import Image from "next/legacy/image";
import type { UserVendorInfoRes } from "gql/User/queries";
import type { IUser } from "types/User.types";
import type { IUserVendorListType } from "types/User.types";
import { DEFAULT_COVER_IMAGE } from "./public-profile.constants";
import { getSocialUsername, hasSocialLinks } from "./public-profile.utils";

export interface PublicProfileSidebarV2Props {
  user: IUser;
  userVendorInfo?: UserVendorInfoRes["userVendorInfo"];
  availableVendors: IUserVendorListType[];
  selectedVendorId: string;
  onVendorChange: (vendorId: string) => void;
}

export function PublicProfileSidebarV2({
  user,
  userVendorInfo,
  availableVendors,
  selectedVendorId,
  onVendorChange,
}: PublicProfileSidebarV2Props) {
  const { t } = useTranslation("common");
  const showSocial = hasSocialLinks(user);

  return (
    <Card size="full" padding="none" shadow="subtle">
      <div
        className="h-40 w-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${user.coverImage || DEFAULT_COVER_IMAGE})`,
        }}
      />
      <div className="flex flex-col gap-3 px-5 pb-6 lg:px-6">
        <div className="mx-auto -mt-16 mb-1">
          <Avatar
            src={user.profileImage}
            name={`${user.name || ""} ${user.lastname || ""}`}
            size="xl"
          />
        </div>

        <h2 className="flex items-center gap-1.5 text-left text-2xl font-bold capitalize tracking-tight text-slate-900 dark:text-white">
          {user.name} {user.lastname || ""}
          {user.dailyQuestionTasks && user.dailyQuestionTasks.length > 0 ? (
            <span title={t("Desafío iniciado")}>⚡️</span>
          ) : null}
        </h2>

        <div className="flex flex-col gap-1.5 text-left text-sm text-slate-600 dark:text-zinc-400">
          <span className="flex items-center gap-2">
            <Mail className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="line-clamp-1">{user.email}</span>
          </span>
          {user.username ? (
            <span className="flex items-center gap-2">
              <AtSign className="h-4 w-4 shrink-0 text-slate-400" />
              {user.username}
            </span>
          ) : null}
        </div>

        {userVendorInfo ? (
          <div className="flex flex-col gap-2.5 text-left">
            <div className="flex items-center gap-2">
              <Image
                src={userVendorInfo.role?.avatar || "/images/jobs/base.png"}
                alt="avatar"
                width={28}
                height={28}
                unoptimized
              />
              <span className="font-semibold text-slate-900 dark:text-white">
                {userVendorInfo.role?.name || t("Ciudadan@")}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {userVendorInfo.seniority ? (
                <LiquidPill
                  as="span"
                  variant="indigo"
                  appearance="tinted"
                  size="xs"
                  icon={<BadgeCheck className="h-3 w-3" />}
                  text={String(userVendorInfo.seniority)}
                />
              ) : (
                <LiquidPill
                  as="span"
                  variant="slate"
                  appearance="tinted"
                  size="xs"
                  text={t("Sin seniority asignado")}
                />
              )}
              <LiquidPill
                as="span"
                variant="slate"
                appearance="tinted"
                size="xs"
                icon={<LanguagesIcon className="h-3 w-3" />}
                text={userVendorInfo.englishLevel || t("Nivel de inglés no asignado")}
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-zinc-400">
              <Cake className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="font-semibold text-slate-900 dark:text-white">
                {t("Cumpleaños")}:
              </span>
              {moment(user.birthday).format("DD/MM/YYYY")}
            </div>
          </div>
        ) : null}

        <div className="text-left">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{t("Acerca de")}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-zinc-400">
            {user.shortBio || t("DEFAULT_SHORT_BIO")}
          </p>
        </div>

        {showSocial ? (
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {t("Enlaces sociales")}
            </p>
            <div className="mt-2 flex flex-col gap-1.5">
              {user.facebook ? (
                <a
                  href={user.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400"
                >
                  <Link2 className="h-4 w-4 shrink-0" />
                  <span className="truncate font-medium">
                    {getSocialUsername(user.facebook, "facebook")}
                  </span>
                </a>
              ) : null}
              {user.linkedin ? (
                <a
                  href={user.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400"
                >
                  <Linkedin className="h-4 w-4 shrink-0 text-[#5764c0]" />
                  <span className="truncate font-medium">
                    {getSocialUsername(user.linkedin, "linkedin")}
                  </span>
                </a>
              ) : null}
              {user.instagram ? (
                <a
                  href={user.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400"
                >
                  <Instagram className="h-4 w-4 shrink-0 text-[#5764c0]" />
                  <span className="truncate font-medium">
                    {getSocialUsername(user.instagram, "instagram")}
                  </span>
                </a>
              ) : null}
              {user.github ? (
                <a
                  href={user.github}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-400"
                >
                  <Github className="h-4 w-4 shrink-0 text-[#5764c0]" />
                  <span className="truncate font-medium">
                    {getSocialUsername(user.github, "github")}
                  </span>
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {availableVendors.length > 0 ? (
          <div className="mt-3 text-left">
            <label className="text-sm font-semibold text-slate-900 dark:text-white">
              {t("Selecciona organización")}
            </label>
            <div className="mt-2">
              <LiquidSelectList
                items={availableVendors.map((vendor) => ({
                  value: vendor.id,
                  label: vendor.name,
                }))}
                onSelect={(value) => onVendorChange(String(value))}
                value={selectedVendorId}
                variant="slate"
                size="sm"
                listSize="sm"
                collapsedShape="pill"
                reactive
                placeholder={t("Selecciona organización")}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
